import { useState } from "react";
import { Link } from "react-router-dom";
import { flights } from "@/data/flights";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { QuoteRequestDialog } from "@/components/common/QuoteRequestDialog";
import { Button } from "@/components/ui/button";
import { Plane, ArrowRight, Clock, ArrowRightLeft } from "lucide-react";

export const FlightDealsSection = () => {
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const featuredFlights = flights.slice(0, 3);

  const handleQuote = (flight) => {
    setSelectedFlight({
      type: "flight",
      from: flight.from,
      to: flight.to,
      airline: flight.airline,
      price: `$${flight.price}`,
    });
    setQuoteDialogOpen(true);
  };

  return (
    <section className="bg-white section-padding" data-testid="flight-deals">
      <div className="container-wide px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="overline mb-3">Great Deals</p>
              <h2 className="heading-lg text-primary">Flight Deals</h2>
            </div>
            <Link
              to="/flights"
              className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-primary transition-colors"
              data-testid="flights-view-all"
            >
              View all deals <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {featuredFlights.map((flight, i) => (
            <AnimatedSection key={flight.id} delay={i * 0.08}>
              <div
                className="group bg-white rounded-xl shadow-card hover:shadow-card-hover transition-shadow duration-200 p-5 flex flex-col h-full"
                data-testid={`flight-card-${i + 1}`}
              >
                {/* Airline header */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-surface-soft flex items-center justify-center">
                      <Plane className="h-4 w-4 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-primary">{flight.airline}</p>
                      <p className="text-xs text-muted-foreground">{flight.cabinClass}</p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-muted-foreground bg-secondary px-3 py-1 rounded-pill">
                    {flight.tripType}
                  </span>
                </div>

                {/* Route */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-0.5">From</p>
                    <p className="text-sm font-semibold text-primary">{flight.from}</p>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <ArrowRightLeft className="h-4 w-4 text-accent" />
                    <div className="h-px w-8 bg-border" />
                  </div>
                  <div className="flex-1 text-right">
                    <p className="text-xs text-muted-foreground mb-0.5">To</p>
                    <p className="text-sm font-semibold text-primary">{flight.to}</p>
                  </div>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-5">
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" /> {flight.duration}
                  </span>
                  <span className="text-border">·</span>
                  <span>{flight.stops}</span>
                </div>

                {/* Price + CTA */}
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-border/60">
                  <div>
                    <p className="text-xs text-muted-foreground">From</p>
                    <p className="text-2xl font-bold text-primary">${flight.price}</p>
                  </div>
                  <Button size="sm" onClick={() => handleQuote(flight)} className="bg-accent text-white hover:bg-accent/90">
                    Get Quote <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <Link
          to="/flights"
          className="sm:hidden flex items-center justify-center gap-2 text-sm font-semibold text-accent mt-6 hover:text-primary transition-colors"
          data-testid="flights-view-all-mobile"
        >
          View All Deals <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <QuoteRequestDialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen} quoteData={selectedFlight} />
    </section>
  );
};
